import { useQuery } from '@tanstack/react-query';
import type { RunSummary } from 'fuel-benchmark-contracts';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { orpc } from '../../../apis/orpc-query';
import { DataTable, type DataTableColumns } from '../../../components/ui/DataTable';
import { InfoCell } from '../../../components/ui/InfoCell';
import { StatusText } from '../../../components/ui/StatusText';
import { formatDay } from '../../../lib/format';
import { useI18nTranslation } from '../../../lib/use-i18n-translation';
import { formatResult, runConditionsSummary, runStatusTone } from '../../runs/run-display';

const PAGE_SIZE = 100;

export interface TestHistoryTabProps {
  vehicleId: string;
}

/** Every run this vehicle has been through — the same table as `/runs`, narrowed to one vehicle. */
export const TestHistoryTab = ({ vehicleId }: TestHistoryTabProps) => {
  const { t } = useI18nTranslation();
  const navigate = useNavigate();

  const runs = useQuery(
    orpc.runs.list.queryOptions({
      input: { page: 1, pageSize: PAGE_SIZE, sortBy: 'testDate', sortDirection: 'desc', vehicleId },
      enabled: vehicleId !== '',
    }),
  );

  const columns: DataTableColumns<RunSummary> = useMemo(
    () => [
      {
        accessorKey: 'testDate',
        header: t('runs.columns.testDate'),
        cell: ({ row }) => <span className="whitespace-nowrap">{formatDay(row.original.testDate)}</span>,
      },
      {
        id: 'scenario',
        accessorFn: (run) => run.scenario.code,
        header: t('runs.columns.scenario'),
        cell: ({ row }) => (
          <InfoCell
            title={row.original.scenario.code}
            subline={runConditionsSummary(row.original, t)}
          />
        ),
      },
      {
        id: 'route',
        accessorFn: (run) => run.route?.code ?? '',
        header: t('runs.columns.route'),
        cell: ({ row }) => <span className="text-muted-foreground">{row.original.route?.code ?? t('common.notSet')}</span>,
      },
      {
        id: 'result',
        accessorFn: (run) => run.result?.value ?? null,
        header: t('runs.columns.result'),
        cell: ({ row }) => <span className="whitespace-nowrap font-medium">{formatResult(row.original.result)}</span>,
      },
      {
        accessorKey: 'status',
        header: t('runs.columns.status'),
        cell: ({ row }) => (
          <StatusText
            label={t(`enums.runStatus.${row.original.status}`)}
            tone={runStatusTone(row.original.status)}
          />
        ),
      },
    ],
    [t],
  );

  return (
    <DataTable
      columns={columns}
      data={runs.data?.items ?? []}
      isLoading={runs.isPending}
      emptyMessage={t('vehicles.history.empty')}
      onRowClick={(run) => navigate(run.status === 'DRAFT' ? `/runs/new?runId=${run.id}` : `/runs/${run.id}`)}
    />
  );
};
